import { get, range } from 'lodash'
import * as Yup from 'yup'
import i18n from 'i18next'

import * as Nope from 'lib/utils/validation/Nope'
import Vehicle from './Vehicle'
import {
    getVehicleDisplayName,
    getEngineSizes,
    VEHICLE_TYPES,
    getVehicleMakeQuickOptions,
    getAlpahebeticalOptionGroups
} from 'lib/utils/store/vehicle'
import { logException } from 'lib/errors'

import getOptionsCaravanVehicleBase from 'graphql/options/getOptionsCaravanVehicleBase.gql'
import getOptionsCaravanVehicleMake from 'graphql/options/getOptionsCaravanVehicleMake.gql'
import getOptionsCaravanVehicleModel from 'graphql/options/getOptionsCaravanVehicleModel.gql'
import getOptionsCaravanVehicleBodyType from 'graphql/options/getOptionsCaravanVehicleBodyType.gql'
import getOptionsCaravanVehicleCommon from 'graphql/options/getOptionsCaravanVehicleCommon.gql'

const yearOptions = range(new Date().getFullYear() + 1, 1899).map(y => ({ name: y, value: y }))

export default class CaravanVehicle extends Vehicle {
    static create (vehicle) {
        return new CaravanVehicle(vehicle)
    }

    /**
     * Common data
     */

    getYear () {
        return get(this, 'vehicle.verticalData.year', null)
    }

    getMakeId () {
        return get(this, 'vehicle.verticalData.make.id', null)
    }

    getMakeName () {
        return get(this, 'vehicle.verticalData.make.name', null)
    }

    getModelId () {
        return get(this, 'vehicle.verticalData.model.id', null)
    }

    getModelName () {
        switch (this.getVehicleTypeId()) {
        case VEHICLE_TYPES.TRAVEL_BOX:
            return get(this, 'vehicle.verticalData.model.name', null)
        case VEHICLE_TYPES.ROLLING_HOME:
        case VEHICLE_TYPES.TRAVEL_TRUCK:
            return get(this, 'vehicle.verticalData.modelInfo', null)
        default:
            return null
        }
    }

    getMakeAndModelName () {
        const make = this.getMakeName() || ''
        const model = this.getModelName() || ''
        return `${make} ${model}`.trim()
    }

    getModelTypeId () {
        return get(this, 'vehicle.verticalData.modelType.id', null)
    }

    getModelInfo () {
        return get(this, 'vehicle.verticalData.modelInfo', null)
    }

    getMakeModelAndYear () {
        const year = this.getYear()
        const makeName = this.getMakeName()
        const modelName = this.getModelName()

        return (makeName || modelName || year)
            ? `${makeName || '-'}, ${modelName || '-'}, ${year || '-'}`
            : null
    }

    getBodyTypeId () {
        return get(this, 'vehicle.verticalData.bodyType.id', null)
    }

    getBodyTypeName (langCode) {
        return get(this, `vehicle.verticalData.bodyType[${langCode}]`, null)
    }

    getBaseId () {
        return get(this, 'vehicle.verticalData.base.id', null)
    }

    getEngineModel () {
        return get(this, 'vehicle.verticalData.engineModel', null)
    }

    /**
     * Technical data
     */

    getFuelTypeId () {
        return get(this, 'vehicle.verticalData.fuelType.id', null)
    }

    getFuelType (langCode) {
        return get(this, `vehicle.verticalData.fuelType[${langCode}]`, null)
    }

    getEngineSize () {
        return get(this, 'vehicle.verticalData.engineSize', null)
    }

    getDriveTypeId () {
        return get(this, 'vehicle.verticalData.driveType.id', null)
    }

    getDriveType (langCode) {
        return get(this, `vehicle.verticalData.driveType[${langCode}]`, null)
    }

    getGearTypeId () {
        return get(this, 'vehicle.verticalData.gearType.id', null)
    }

    getGearType (langCode) {
        return get(this, `vehicle.verticalData.gearType[${langCode}]`, null)
    }

    getPower () {
        return get(this, 'vehicle.verticalData.power', null)
    }

    getTorque () {
        return get(this, 'vehicle.verticalData.torque', null)
    }

    getPowerAndTorque () {
        const power = this.getPower()
        const torque = this.getTorque()
        return power || torque ? `${power ? `${power} (kW)` : '- '}, ${torque ? `${torque} (Nm)` : '-'}` : null
    }

    getTopSpeed () {
        return get(this, 'vehicle.verticalData.topSpeed', null)
    }

    getAcceleration () {
        return get(this, 'vehicle.verticalData.acceleration', null)
    }

    getTopSpeedAndAcceleration () {
        const topSpeed = this.getTopSpeed()
        const acceleration = this.getAcceleration()

        return topSpeed || acceleration
            ? `${topSpeed ? `${topSpeed} (km/h)` : '- '}, ${acceleration ? `${acceleration} s (0-100)` : '-'}`
            : null
    }

    getDoors () {
        return get(this, 'vehicle.verticalData.doors', null)
    }

    getSeats () {
        return get(this, 'vehicle.verticalData.seats', null)
    }

    getDeadWeight () {
        return get(this, 'vehicle.verticalData.deadWeight', null)
    }

    getGrossWeight () {
        return get(this, 'vehicle.verticalData.weight', null)
    }

    getTowWeightWithBrakes () {
        return get(this, 'vehicle.verticalData.towCapacityWithBrake', null)
    }

    getTowWeightWithoutBrakes () {
        return get(this, 'vehicle.verticalData.towCapacityBrakeLess', null)
    }

    getTowWithBrakesAndBrakeless () {
        const withBrakes = this.getTowWeightWithBrakes()
        const withoutBrakes = this.getTowWeightWithoutBrakes()

        return withBrakes || withoutBrakes
            ? `${withoutBrakes ? `${withoutBrakes} (kg)` : '- '}, ${withBrakes ? `${withBrakes} (kg)` : '-'}`
            : null
    }

    getWeightCurbGross () {
        const dead = this.getDeadWeight()
        const gross = this.getGrossWeight()

        return dead || gross
            ? `${dead ? `${dead} (kg)` : '- '}, ${gross ? `${gross} (kg)` : '-'}`
            : null
    }

    getWidth () {
        return get(this, 'vehicle.verticalData.width', null)
    }

    getLength () {
        return get(this, 'vehicle.verticalData.length', null)
    }

    getLengthAndWidth () {
        const length = this.getLength()
        const width = this.getWidth()
        return length || width
            ? `${length ? `${length} (m)` : '- '}, ${width ? `${width} (m)` : '-'}`
            : null
    }

    getSpeedLimit () {
        return get(this, 'vehicle.verticalData.speedLimit', null)
    }

    getBeds () {
        return get(this, 'vehicle.verticalData.beds', null)
    }

    /**
     * Accessories
     */

    getAccessories () {
        return get(this, 'vehicle.verticalData.accessories', null)
    }

    /**
     * Primary details
     */

    getPrimaryDetailsSummary () {
        return this.getMakeAndModelName() || '-'
    }

    getPrimaryDetailsValidationSchema () {
        return Nope.schema({
            vehicleType: Yup.string(),
            regNumber: Nope.registerNumber(),
            isFinnishRegNumber: Yup.boolean(),
            name: Nope.vehicleName(),
            bodyType: Yup.string(),
            make: Yup.string(),
            mfgYear: Yup.string().nullable(),
            modelType: Yup.string(),
            model: Yup.string(),
            caravan: Yup.object().shape({
                base: Yup.string(),
                engineModel: Yup.string(),
                modelInfo: Yup.string(),
            })
        })
    }

    getPrimaryDetailsInitialValues () {
        return {
            vehicleType: this.getVehicleTypeId() || '',
            make: this.getMakeId() || '',
            mfgYear: this.getYear() || '',
            name: this.vehicle.name || '',
            bodyType: this.getBodyTypeId() || '',
            regNumber: this.vehicle.regNumber || '',
            model: this.getModelId() || '',
            isFinnishRegNumber: this.getIsFinnishRegNumber(),
            caravan: {
                base: this.getBaseId() || '',
                engineModel: this.getEngineModel() || '',
                modelInfo: this.getModelInfo() || '',
            }
        }
    }

    getPrimaryDetailsFieldsLayoutSchema () {
        return {
            layout: [
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['regNumber', 'isFinnishRegNumber', 'name'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['make', 'model', 'caravan.modelInfo', 'caravan.engineModel', 'bodyType', 'caravan.base', 'caravan.engineModel', 'mfgYear'],
                },
            ]
        }
    }

    getSubTypeDetailsFieldsSchema (t) {
        let fields = []

        switch (this.getVehicleTypeId()) {
        case VEHICLE_TYPES.ROLLING_HOME:
            fields = [
                {
                    name: 'caravan.modelInfo',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:vehicleModel'),
                        placeholder: t('vehicleProfile:vehicleModel'),
                    },
                },
            ]
            break
        case VEHICLE_TYPES.TRAVEL_TRUCK:
            fields = [
                {
                    name: 'caravan.base',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:base'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.base'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.base'),
                        placeholder: t('vehicleProfile:sections.primary.choose.base'),
                    },
                    query: {
                        query: getOptionsCaravanVehicleBase,
                        variables: ({ values }) => {
                            return {
                                id: values.vehicleType
                            }
                        },
                        getResultValue: (data) => {
                            return get(data, 'options.caravan.base', [])
                        },
                    },
                },
                {
                    name: 'caravan.modelInfo',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:vehicleModel'),
                        placeholder: t('vehicleProfile:vehicleModel'),
                    },
                },
                {
                    name: 'caravan.engineModel',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:vehicleModelType'),
                        placeholder: t('vehicleProfile:vehicleModelType'),
                    },
                },
                {
                    name: 'bodyType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionNameKey: i18n.language,
                        optionValueKey: 'id',
                        label: t('vehicleProfile:bodyType'),
                        placeholder: t('vehicleProfile:sections.primary.choose.bodyType'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.bodyType'),
                    },
                    query: {
                        query: getOptionsCaravanVehicleBodyType,
                        variables: ({ values }) => {
                            return {
                                id: values.vehicleType
                            }
                        },
                        getResultValue: (data) => {
                            return get(data, 'options.caravan.bodyType', [])
                        },
                    },
                },
            ]
            break
        case VEHICLE_TYPES.TRAVEL_BOX:
            fields = [
                {
                    name: 'model',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        label: t('vehicleProfile:vehicleModel'),
                        placeholder: t('vehicleProfile:sections.primary.choose.model'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.model'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.model'),
                    },
                    requireFields: ['make'],
                    query: {
                        query: getOptionsCaravanVehicleModel,
                        variables: ({ values }) => {
                            return {
                                id: values.make
                            }
                        },
                        getResultValue: (data) => {
                            return get(data, 'options.caravan.model', [])
                        },
                    },
                },
                {
                    name: 'caravan.engineModel',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:vehicleModelType'),
                        placeholder: t('vehicleProfile:vehicleModelType'),
                    },
                }
            ]
            break
        default:
            logException(`Invalid vehicle type: ${this.getVehicleTypeId()}`)
            break
        }

        return fields
    }

    getPrimaryDetailsFieldsSchema (t) {
        const subTypeFields = this.getSubTypeDetailsFieldsSchema(t)
        return {
            fields: [
                {
                    name: 'regNumber',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:regNumber'),
                        placeholder: 'ABC-123',
                        validate: (value, formValues) => this.validateRegNumber(value, formValues),
                    },
                },
                {
                    name: 'isFinnishRegNumber',
                    type: 'boolean',
                    inputType: 'checkbox',
                    fieldProps: {
                        label: t('vehicleProfile:sections.primary.isFinnishRegNumber'),
                        size: 'large'
                    }
                },
                {
                    name: 'name',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:vehicleNickname'),
                        description: t('vehicleProfile:vehicleNicknameDescription'),
                        placeholder: getVehicleDisplayName(this.vehicle, t),
                    },
                },
                {
                    name: 'make',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        label: t('vehicleProfile:vehicleMake'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.make'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.make'),
                        placeholder: t('vehicleProfile:sections.primary.choose.make'),
                    },
                    resetFieldsOnChange: ['model'],
                    query: {
                        query: getOptionsCaravanVehicleMake,
                        variables: ({ values }) => {
                            return {
                                id: parseInt(values.vehicleType)
                            }
                        },
                        getResultValue: (data) => {
                            const mostPopular = getVehicleMakeQuickOptions(get(data, 'options.caravan.make', []), t)
                            const groupedMakes = getAlpahebeticalOptionGroups(get(data, 'options.caravan.make', []), 'name')
                            return [...mostPopular, ...groupedMakes]
                        },
                    },
                },
                ...subTypeFields,
                {
                    name: 'mfgYear',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'value',
                        label: t('vehicleProfile:vehicleYearModel'),
                        placeholder: t('vehicleProfile:sections.primary.choose.year'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.year'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.year'),
                        options: yearOptions,
                    },
                    requireFields: [],
                    resetFieldsOnChange: [],
                },
            ]
        }
    }

     /**
     * Technical details
     */

    getTechnicalDetailsValidationSchema () {
        return Nope.schema({
            power: Nope.positiveInteger(),
            torque: Nope.positiveInteger(),
            topSpeed: Nope.positiveInteger(),
            acceleration: Nope.positiveNumber(),
            seats: Nope.positiveInteger(),
            doors: Nope.positiveInteger(),
            curbWeight: Nope.positiveInteger(),
            grossWeight: Nope.positiveInteger(),
            towWeightWithBrakes: Nope.positiveInteger(),
            caravan: Yup.object().shape({
                beds: Nope.positiveInteger(),
                length: Nope.positiveNumber(),
                width: Nope.positiveNumber(),
                speedLimit: Nope.positiveInteger(),
                weight: Nope.positiveInteger(),
                deadWeight: Nope.positiveInteger(),
                towCapacityBrakeLess: Nope.positiveInteger(),
                towCapacityWithBrake: Nope.positiveInteger(),
            }),
        })
    }

    getTechnicalDetailsInitialValues () {
        return {
            power: this.getPower() || '',
            torque: this.getTorque() || '',
            topSpeed: this.getTopSpeed() || '',
            acceleration: this.getAcceleration() || '',
            seats: this.getSeats() || '',
            doors: this.getDoors() || '',
            fuelType: this.getFuelTypeId() || '',
            gearType: this.getGearTypeId() || '',
            driveType: this.getDriveTypeId() || '',
            engineSize: this.getEngineSize() || '',
            caravan: {
                beds: this.getBeds() || '',
                length: this.getLength() || '',
                width: this.getWidth() || '',
                speedLimit: this.getSpeedLimit() || '',
                weight: this.getGrossWeight() || '',
                deadWeight: this.getDeadWeight() || '',
                towCapacityBrakeLess: this.getTowWeightWithoutBrakes() || '',
                towCapacityWithBrake: this.getTowWeightWithBrakes() || '',
            }
        }
    }

    getTechnicalDetailsFieldsLayoutSchema (t) {
        return {
            layout: [
                {
                    type: 'formElementGroup',
                    classNames: 'cols-3',
                    fields: ['power', 'torque', 'caravan.speedLimit'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['engineSize', 'fuelType'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['driveType', 'gearType'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['seats', 'doors', 'caravan.beds'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['caravan.length', 'caravan.width']
                },
                {
                    type: 'formElementGroup',
                    title: t('vehicleProfile:weight'),
                    classNames: 'cols-2',
                    fields: ['caravan.deadWeight', 'caravan.weight'],
                },
                {
                    type: 'formElementGroup',
                    title: t('vehicleProfile:towCapacity'),
                    classNames: 'cols-2',
                    fields: ['caravan.towCapacityBrakeLess', 'caravan.towCapacityWithBrake'],
                },
            ]
        }
    }

    getSubtypesTechnicalDetailsFieldsSchema (t) {
        let fields = []
        switch (this.getVehicleTypeId()) {
        case VEHICLE_TYPES.TRAVEL_BOX:
            break
        case VEHICLE_TYPES.TRAVEL_TRUCK:
            fields = [
                {
                    name: 'power',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:power')} (kW)`,
                    },
                },
                {
                    name: 'torque',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:torque')} (Nm)`,
                    },
                },
                {
                    name: 'caravan.speedLimit',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'code',
                        optionNameKey: 'name',
                        label: `${t('vehicleProfile:speedLimit')} (km/h)`,
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.speedLimit'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.speedLimit'),
                        placeholder: t('vehicleProfile:sections.technical.choose.speedLimit'),
                    },
                    query: {
                        query: getOptionsCaravanVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.caravan.speedLimit', [])
                        },
                    },
                },
                {
                    name: 'seats',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: t('vehicleProfile:seats'),
                    },
                },
                {
                    name: 'doors',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: t('vehicleProfile:doors'),
                    },
                },
                {
                    name: 'engineSize',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        label: t('vehicleProfile:engineSize'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.engineSize'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.engineSize'),
                        placeholder: t('vehicleProfile:sections.technical.choose.engineSize'),
                        options: [
                            { value: '0', name: t('vehicleProfile:sections.technical.noEngineSize') },
                            ...getEngineSizes(this.vehicle)
                        ]
                    },
                },
                {
                    name: 'fuelType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:fuelType'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.fuelType'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.fuelType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.fuelType'),
                    },
                    query: {
                        query: getOptionsCaravanVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.caravan.fuelType', [])
                        },
                    },
                },
                {
                    name: 'driveType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:driveType'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.driveType'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.driveType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.driveType'),
                    },
                    query: {
                        query: getOptionsCaravanVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.caravan.driveType', [])
                        },
                    },
                },
                {
                    name: 'gearType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:gearType'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.gearType'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.gearType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.gearType'),
                    },
                    query: {
                        query: getOptionsCaravanVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.caravan.gearType', [])
                        },
                    },
                },
                {
                    name: 'caravan.towCapacityWithBrake',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:towWeightWithBrakes')} (kg)`,
                    },
                },
                {
                    name: 'caravan.towCapacityBrakeLess',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:towWeightWithoutBrakes')} (kg)`,
                    },
                },
            ]
            break
        case VEHICLE_TYPES.ROLLING_HOME:
            break
        }

        return fields
    }

    getTechnicalDetailsFieldsSchema (t) {
        return {
            fields: [
                ...this.getSubtypesTechnicalDetailsFieldsSchema(t),
                {
                    name: 'caravan.deadWeight',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:curbWeight')} (kg)`,
                    },
                },
                {
                    name: 'caravan.weight',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:grossWeight')} (kg)`,
                    },
                },
                {
                    name: 'caravan.beds',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: t('vehicleProfile:beds')
                    }
                },
                {
                    name: 'caravan.length',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: t('vehicleProfile:length')
                    }
                },
                {
                    name: 'caravan.width',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: t('vehicleProfile:width')
                    }
                },
            ],
        }
    }
}
